import { httpClient } from '@shared/api';
import type {
  Channel,
  ChannelCreateRequest,
  ChannelStats,
  ChannelUpdateRequest,
} from '../model/types';

/**
 * Все channels-эндпоинты бэка richat-next.
 *
 * Соответствует richat_backend/api/routers/channels.py.
 *
 * Префикс роутера на бэке — /channels/, не /api/channels/. Это потому
 * что nginx режет /api/ перед проксированием (rewrite в location /api/).
 *
 * ВАЖНО: GET /channels/{id} на бэке НЕ существует. Один канал получается
 * через list().find() — реализовано в useChannel() в queries.ts. Это
 * сознательное архитектурное решение: один канал — лёгкий объект,
 * нет смысла делать отдельный round-trip.
 */
export const channelApi = {
  /** GET /channels?assistant_id=... — список каналов ассистента. */
  async list(assistantId: string): Promise<Channel[]> {
    const response = await httpClient.get<Channel[]>('/channels', {
      params: { assistant_id: assistantId },
    });
    return response.data;
  },

  /**
   * POST /channels — подключить новый канал.
   *
   * Для MAX/Telegram: синхронная валидация токена + регистрация webhook'а
   * (для MAX) или подъём long-polling worker'а на Railway (для TG). ~500-2000ms.
   *
   * Для widget: бэк генерирует publishable_key и создаёт запись в widget_configs
   * с дефолтами. ~50-100ms. Если присланы allowed_origins / display_name —
   * проставляются сразу, иначе дефолты.
   *
   * 400 — токен невалиден / бот уже подключён / bridge временно недоступен.
   *       detail содержит человеко-читаемую причину.
   */
  async create(payload: ChannelCreateRequest): Promise<Channel> {
    const response = await httpClient.post<Channel>('/channels', payload);
    return response.data;
  },

  /** GET /channels/{id}/stats — агрегаты для карточки канала. */
  async getStats(channelId: string): Promise<ChannelStats> {
    const response = await httpClient.get<ChannelStats>(`/channels/${channelId}/stats`);
    return response.data;
  },

  /**
   * PATCH /channels/{id} — переключить статус active ↔ paused.
   * Disabled через PATCH не ставится — это системная установка при сбое.
   */
  async update(channelId: string, payload: ChannelUpdateRequest): Promise<Channel> {
    const response = await httpClient.patch<Channel>(`/channels/${channelId}`, payload);
    return response.data;
  },

  /**
   * DELETE /channels/{id} — отключить и удалить канал.
   *
   * Для MAX: пытается отписать webhook у MAX (best-effort).
   * Для Telegram: шлёт manage-bot stop на Railway-bridge (best-effort).
   * Для widget: просто удаляет записи widget_configs + channel.
   *
   * Запись в БД удаляется в любом случае — это позволяет переподключить
   * канал, даже если внешний сервис временно недоступен.
   */
  async delete(channelId: string): Promise<void> {
    await httpClient.delete(`/channels/${channelId}`);
  },
};
